import { useEffect, useRef } from "react";
import "./ThunderAnimation.css";

export default function ThunderAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for defender
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of defender sprite
    const frameWidth = 192;  // Width of each frame
    const frameHeight = 192; // Height of the sprite
    const centerX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const centerY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];

    // Initial attacker animation - electric charge buildup
    attackerEl.style.transition = 'transform 0.4s ease-in-out, filter 0.4s ease-in-out';
    attackerEl.style.transform = 'scale(1.1)';
    attackerEl.style.filter = 'brightness(1.3) drop-shadow(0 0 15px #FFD700)';
    
    // Create electric aura around attacker
    const electricAura = document.createElement('div');
    electricAura.className = 'thunder-electric-aura';
    const attackerRect = attackerEl.getBoundingClientRect();
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    electricAura.style.left = `${attackerCenterX}px`;
    electricAura.style.top = `${attackerCenterY}px`;
    container.appendChild(electricAura);
    elements.push(electricAura);
    
    // Create charging sparks around attacker
    for (let i = 0; i < 8; i++) {
      const sparkTimer = setTimeout(() => {
        const spark = document.createElement('div');
        spark.className = 'thunder-charge-spark';
        
        const angle = (i / 8) * Math.PI * 2;
        const distance = 40 + Math.random() * 20;
        spark.style.left = `${attackerCenterX + Math.cos(angle) * distance}px`;
        spark.style.top = `${attackerCenterY + Math.sin(angle) * distance}px`;
        spark.style.setProperty('--spark-delay', `${i * 0.05}s`);
        
        container.appendChild(spark);
        elements.push(spark);
        
        // Auto-remove spark
        const sparkRemoveTimer = setTimeout(() => {
          if (spark.isConnected) {
            spark.remove();
          }
        }, 800);
        timers.push(sparkRemoveTimer);
      }, i * 50);
      timers.push(sparkTimer);
    }
    
    // Main Thunder attack with sprite animation
    const thunderTimer = setTimeout(() => {
      // Create screen flash effect
      const screenFlash = document.createElement('div');
      screenFlash.className = 'thunder-screen-flash';
      container.appendChild(screenFlash);
      elements.push(screenFlash);
      
      // Create main thunder sprite animation
      const thunder = document.createElement("div");
      thunder.className = "thunder-sprite-anim";
      thunder.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
      thunder.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
      container.appendChild(thunder);
      elements.push(thunder);
      animRef.current = thunder;
      
      // Create multiple lightning bolts from above
      for (let i = 0; i < 3; i++) {
        const boltTimer = setTimeout(() => {
          const bolt = document.createElement('div');
          bolt.className = 'thunder-lightning-bolt';
          
          const offsetX = (i - 1) * 30 + (Math.random() - 0.5) * 20;
          bolt.style.left = `${centerX + offsetX}px`;
          bolt.style.top = `${centerY - 200}px`; // Start from high above
          bolt.style.setProperty('--bolt-height', '250px');
          bolt.style.setProperty('--bolt-delay', `${i * 0.1}s`);
          
          container.appendChild(bolt);
          elements.push(bolt);
          
          // Auto-remove bolt
          const boltRemoveTimer = setTimeout(() => {
            if (bolt.isConnected) {
              bolt.remove();
            }
          }, 1000);
          timers.push(boltRemoveTimer);
        }, i * 100);
        timers.push(boltTimer);
      }
      
    }, 500); // After charge buildup
    
    timers.push(thunderTimer);

    // Impact effects and paralysis potential
    const impactTimer = setTimeout(() => {
      // Electric explosion on impact
      const explosion = document.createElement('div');
      explosion.className = 'thunder-electric-explosion';
      explosion.style.left = `${centerX}px`;
      explosion.style.top = `${centerY}px`;
      container.appendChild(explosion);
      elements.push(explosion);
      
      // Paralysis indicator (30% chance for visual effect)
      const paralysisChance = Math.random();
      if (paralysisChance < 0.3) {
        const paralysisEffect = document.createElement('div');
        paralysisEffect.className = 'thunder-paralysis';
        paralysisEffect.style.left = `${centerX}px`;
        paralysisEffect.style.top = `${centerY - 50}px`;
        container.appendChild(paralysisEffect);
        elements.push(paralysisEffect);
        
        // Enhanced explosion for paralysis
        explosion.classList.add('paralysis-explosion');
      }
      
      // Make defender react with electric shock
      defenderEl.classList.add('thunder-hit');
      defenderEl.style.filter = 'brightness(2) contrast(1.5) saturate(0.5)';
      
      // Create electric discharge particles
      for (let i = 0; i < 15; i++) {
        const particle = document.createElement('div');
        particle.className = 'thunder-discharge-particle';
        
        const angle = Math.random() * Math.PI * 2;
        const distance = 15 + Math.random() * 45;
        particle.style.left = `${centerX + Math.cos(angle) * distance}px`;
        particle.style.top = `${centerY + Math.sin(angle) * distance}px`;
        
        particle.style.setProperty('--particle-rotation', `${Math.random() * 360}deg`);
        particle.style.setProperty('--particle-scale', `${0.6 + Math.random() * 0.8}`);
        particle.style.setProperty('--drift-x', `${(Math.random() - 0.5) * 50}px`);
        particle.style.setProperty('--drift-y', `${(Math.random() - 0.5) * 50}px`);
        
        container.appendChild(particle);
        elements.push(particle);
        
        const particleTimer = setTimeout(() => {
          if (particle.parentNode) {
            particle.remove();
          }
        }, 700 + Math.random() * 300);
        timers.push(particleTimer);
      }
      
      // Create electric afterglow
      const afterglow = document.createElement('div');
      afterglow.className = 'thunder-electric-afterglow';
      afterglow.style.left = `${centerX}px`;
      afterglow.style.top = `${centerY}px`;
      container.appendChild(afterglow);
      elements.push(afterglow);
      
      // Reset defender after hit effect
      const defenderResetTimer = setTimeout(() => {
        defenderEl.classList.remove('thunder-hit');
        defenderEl.style.filter = '';
      }, 600);
      timers.push(defenderResetTimer);
      
    }, 800); // Impact timing
    
    timers.push(impactTimer);

    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.3s ease-out, filter 0.3s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 900);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 1800);
    timers.push(completeTimer);

    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('thunder-hit');
        defenderEl.style.filter = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining effects
      const remainingElements = container.querySelectorAll(
        '.thunder-electric-aura, .thunder-charge-spark, .thunder-screen-flash, ' +
        '.thunder-sprite-anim, .thunder-lightning-bolt, .thunder-electric-explosion, ' +
        '.thunder-paralysis, .thunder-discharge-particle, .thunder-electric-afterglow'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

ThunderAnimation.moveType = "electric";
